import React, { useCallback, useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Download, ExternalLink, Trash, Save } from "lucide-react";
import { formatPrice } from "@/lib/settings";

const STATUSES = ["yeni", "arandi", "sipariş onaylandı", "iptal edildi", "tamamlandı"];

export default function AdminQuotes() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/quotes");
    setItems(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      await api.patch(`/quotes/${selected.id}`, { status: selected.status, note: selected.note || "" });
      toast.success("Kaydedildi");
      load();
    } catch { toast.error("Kayıt hatası"); }
  };

  const del = async (id) => {
    if (!window.confirm("Silmek istiyor musunuz?")) return;
    await api.delete(`/quotes/${id}`);
    toast.success("Silindi");
    setSelected(null);
    load();
  };

  return (
    <div data-testid="admin-quotes">
      <h1 className="font-display text-3xl md:text-4xl font-black text-ink-900 mb-8">Teklifler & Siparişler</h1>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-3xl bg-white border border-ink-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-ink-50">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">No</th>
                  <th className="text-left px-4 py-3 font-semibold">Ürün</th>
                  <th className="text-left px-4 py-3 font-semibold">Fiyat</th>
                  <th className="text-left px-4 py-3 font-semibold">Durum</th>
                  <th className="text-left px-4 py-3 font-semibold">Tarih</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.map((q) => (
                  <tr
                    key={q.id}
                    onClick={() => setSelected(q)}
                    className={`border-t border-ink-100 cursor-pointer hover:bg-brand-50/30 ${selected?.id === q.id ? "bg-brand-50/60" : ""}`}
                    data-testid={`quote-row-${q.id}`}
                  >
                    <td className="px-4 py-3 font-mono text-xs text-brand-600">{q.quote_no}</td>
                    <td className="px-4 py-3">{q.input?.furniture_type_name}<div className="text-xs text-ink-500">{q.input?.variation}</div></td>
                    <td className="px-4 py-3 font-semibold">{formatPrice(q.calc?.total, q.currency)}</td>
                    <td className="px-4 py-3"><span className="rounded-full bg-brand-50 text-brand-700 px-2 py-0.5 text-[11px] font-bold">{q.status}</span></td>
                    <td className="px-4 py-3 text-ink-500">{new Date(q.created_at).toLocaleString("tr-TR")}</td>
                    <td className="px-4 py-3">
                      <a
                        onClick={(e) => e.stopPropagation()}
                        href={`${API}/quotes/${q.id}/pdf`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-brand-600 text-xs font-semibold"
                      ><Download className="h-3.5 w-3.5" /> PDF</a>
                    </td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr><td colSpan={6} className="text-center py-12 text-ink-500">Henüz teklif yok.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="rounded-3xl bg-white border border-ink-100 p-6">
          {selected ? (
            <div className="space-y-4" data-testid="quote-detail">
              <div>
                <div className="text-xs text-ink-500">Teklif No</div>
                <div className="font-mono font-bold text-brand-600">{selected.quote_no}</div>
              </div>
              <div className="text-sm space-y-1.5">
                <div><b>Ürün:</b> {selected.input.furniture_type_name}</div>
                {selected.input.variation && <div><b>Alt Model:</b> {selected.input.variation}</div>}
                <div><b>Ölçü:</b> {selected.input.width_cm}×{selected.input.height_cm}×{selected.input.depth_cm} cm</div>
                <div><b>Renk:</b> {selected.input.color_name}</div>
                <div><b>Kapak:</b> {selected.input.cover_type_name}</div>
                <div><b>Ayak / Kulp:</b> {selected.input.leg_type_name} / {selected.input.handle_type_name}</div>
                {selected.input.customer_name && <div><b>Müşteri:</b> {selected.input.customer_name}</div>}
                {selected.input.customer_phone && <div><b>Tel:</b> {selected.input.customer_phone}</div>}
                <div className="pt-2 text-lg font-display font-bold text-brand-600">Toplam: {formatPrice(selected.calc.total, selected.currency)}</div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest font-bold">Durum</Label>
                <Select value={selected.status} onValueChange={(v) => setSelected({ ...selected, status: v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest font-bold">Not</Label>
                <Textarea rows={3} value={selected.note || ""} onChange={(e) => setSelected({ ...selected, note: e.target.value })} className="mt-1.5" />
              </div>
              <div className="flex gap-2">
                <Button data-testid="quote-save" onClick={save} className="gradient-primary text-white rounded-full"><Save className="h-4 w-4 mr-1.5" /> Kaydet</Button>
                <a href={`${API}/quotes/${selected.id}/pdf`} target="_blank" rel="noreferrer">
                  <Button variant="outline" className="rounded-full"><ExternalLink className="h-4 w-4 mr-1.5" /> PDF</Button>
                </a>
                <Button variant="outline" className="rounded-full text-red-600 border-red-300 hover:bg-red-50" onClick={() => del(selected.id)}><Trash className="h-4 w-4" /></Button>
              </div>
            </div>
          ) : (
            <div className="text-ink-500 text-sm">Detay için soldan bir teklif seçin.</div>
          )}
        </div>
      </div>
    </div>
  );
}
